import {useEffect, useMemo, useRef} from "react";
import GraphicUtils from "../../../../../utils/graphic-utils.js";
import ReportUtils from "../../../../../utils/report-utils.js";

function ReportCard({ title, idTitle = "", value }) {
    return (
        <div className="mb-1 mt-3">
            <div className="text-center cursor-pointer" title={idTitle}>
                <div className="box boxCount">
                    <h3>{title}</h3>
                    <h2>{value}</h2>
                </div>
            </div>
        </div>
    )
}

export default function ReportContent({ data, collapsed }) {
    const sumaryCards = [
        {
            title: "Total de veículos",
            value: Number(data?.totalVehicles?.[0]?.qty ?? 0),
        },
        {
            title: "Total de ocorrências",
            value: Number(data?.totalOccurrences?.[0]?.vehicle_occ ?? 0),
        },
        {
            title: "Com mais de um veículo",
            value: Number(data?.totalOccurrencesWithMultiVehicles?.[0]?.multi_vehicle_occ ?? 0),
        },
        {
            title: "Com mais de uma natureza",
            value: Number(data?.totalOccurrencesWithMultiReasons?.[0]?.multi_reasons_occ ?? 0),
        },
    ];

    const vehicleClassificationChart = useMemo(() => {
        const source = data?.vehicleClassification || [];

        return {
            labels: source.map(item => ReportUtils.formatText(item.classif)),
            values: source.map(item => Number(item.qty || 0)),
        }
    }, [data]);

    const vehicleTypesChart = useMemo(() => {
        const source = data?.vehicleTypes || [];

        return {
            labels: source.map(item => ReportUtils.formatText(item.type)),
            values: source.map(item => Number(item.qty || 0)),
        }
    }, [data]);

    const vehicleTypesByReasonChart = useMemo(() => {
        const source = data?.vehicleTypesByReason || [];

        const reasons = [...new Set(source.map(item => item.reasons))];
        const vehicles = [...new Set(source.map(item => item.vehicles))];

        const values = vehicles.map(vehicle => {
            const serie = [vehicle];

            reasons.forEach(reason => {
                const found = source.find(
                    item => item.vehicles === vehicle && item.reasons === reason
                );

                serie.push(Number(found?.qty || 0));
            });

            return serie;
        });

        return {
            labels: reasons,
            values,
        }
    }, [data]);

    const vehicleTypesByReasonExport = useMemo(() => {
        return {
            headers: ["Descrição", ...vehicleTypesByReasonChart.labels],
            rows: vehicleTypesByReasonChart.values,
        };
    }, [vehicleTypesByReasonChart]);

    const vehicleBrandModelChart = useMemo(() => {
        const source = data?.vehicleBrandModel || [];

        return {
            labels: source.map(item => ReportUtils.formatText(item.type)),
            values: source.map(item => Number(item.qty || 0)),
        }
    }, [data]);

    useEffect(() => {
        ReportUtils.createExportableTable(
            sumaryCards.map(item => item.title),
            sumaryCards.map(item => item.value),
            "Cards",
        );

        GraphicUtils.barChart(
            "vehicle-classification-chart",
            vehicleClassificationChart.values,
            vehicleClassificationChart.labels,
            false,
        );

        if (vehicleClassificationChart.values.some(v => v > 0)) {
            ReportUtils.createExportableTable(
                vehicleClassificationChart.labels,
                vehicleClassificationChart.values,
                "Classificação dos veículos ",
            );
        }

        GraphicUtils.barChart(
            "vehicle-types-chart",
            vehicleTypesChart.values,
            vehicleTypesChart.labels,
            false,
        );

        if (vehicleTypesChart.values.some(v => v > 0)) {
            ReportUtils.createExportableTable(
                vehicleTypesChart.labels,
                vehicleTypesChart.values,
                "Tipos de veículos",
            );
        }

        GraphicUtils.stackedBarChart(
            "vehicle-types-by-reason",
            vehicleTypesByReasonChart.values,
            vehicleTypesByReasonChart.labels,
            "Quantidade de veículos",
            "",
        );

        if (
            vehicleTypesByReasonChart.values.some(serie =>
                serie.slice(1).some(v => v > 0)
            )
        ) {
            ReportUtils.createExportableTable(
                vehicleTypesByReasonExport.headers,
                vehicleTypesByReasonExport.rows,
                "Tipos de veículos por natureza da ocorrência",
            );
        }

        GraphicUtils.barChart(
            "vehicle-brand-model",
            vehicleBrandModelChart.values,
            vehicleBrandModelChart.labels,
            true,
        );

        if (vehicleBrandModelChart.values.some(v => v > 0)) {
            ReportUtils.createExportableTable(
                vehicleBrandModelChart.labels,
                vehicleBrandModelChart.values,
                "Marca e modelo dos veículos",
            );
        }


    }, [vehicleClassificationChart, vehicleTypesChart, vehicleTypesByReasonChart, vehicleTypesByReasonExport, vehicleBrandModelChart, collapsed]);

    return (
        <>
            <div className="space-y-6">
                <div className="grid grid-cols-4 gap-2">
                    <ReportCard
                        title="Total de veículos"
                        value={data?.totalVehicles?.[0]?.qty ?? ""}
                    />
                    <ReportCard
                        title="Total de ocorrências"
                        idTitle="Ocorrências envolvendo veículos"
                        value={data?.totalOccurrences?.[0]?.vehicle_occ ?? ""}
                    />
                    <ReportCard
                        title="Com mais de um veículo"
                        idTitle="Ocorrências envolvendo vários veículos"
                        value={data?.totalOccurrencesWithMultiVehicles?.[0]?.multi_vehicle_occ ?? 0}
                    />
                    <ReportCard
                        title="Com mais de uma natureza"
                        idTitle="Ocorrências com veículos envolvendo várias naturezas"
                        value={data?.totalOccurrencesWithMultiReasons?.[0]?.multi_reasons_occ ?? 0}
                    />
                </div>
                <div className="chart-divisor" />
                <div>
                    <div>
                        <span className="chart-title">Classificação dos veículos na ocorrência</span>
                        <div id="vehicle-classification-chart" />
                    </div>
                </div>
                <div className="chart-divisor" />
                <div>
                    <span className="chart-title">Tipos de veículos</span>
                    <div id="vehicle-types-chart" />
                </div>
                <div className="chart-divisor" />
                <div>
                    <span className="chart-title">Tipos de veículos por natureza da ocorrência</span>
                    <div id="vehicle-types-by-reason" />
                    <div className="obs-warn mt-4">
                        * Veículos associados a ocorrências com mais de uma natureza serão contabilizados em cada uma delas.
                    </div>
                </div>
                <div className="chart-divisor" />
                <div>
                    <span className="chart-title">Marca e modelo dos veículos associados</span>
                    <div id="vehicle-brand-model" />
                </div>
            </div>
            <div id="chart-tables" />
        </>
    )
}